/**
 * Returns — wireframe 4 (W2-12): the period list, the gates panel, the
 * lifecycle actions.
 *
 * The gates panel is DRIVEN BY REAL BLOCKERS — the same assemble/tie-out the
 * prepare transition runs — so what the button refuses and what the panel
 * shows are one computation (ADR-0014: the screen renders, it never decides).
 *
 * **Dressed in the legacy's grammar** (ADR-0020, Level 1): navy table head,
 * money cards, banded section rows, a green export control. The dress is
 * inherited; the refusals underneath are ours.
 *
 * The NET OUTWARD / GROSS TOTAL rows the legacy closes on are here — and they
 * arrive **computed**, from `assemble_draft`. This screen renders them and
 * sums nothing (ADR-0014): netting credit notes is a taxable-value decision,
 * and the browser is the wrong place to guess a sign convention.
 */

import { Alert, Button, Card } from '@fiveox/ui'
import { FileJson, FileSpreadsheet, Plus } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'

import { PERIOD, useEvaluation } from '../lib/access'
import {
  ApiError,
  type ClientRecord,
  type PeriodGates,
  type PeriodRecord,
  type PeriodTables,
  type ClientGstr1Check,
  type ErrorReportUpload,
  type AdvanceEntry,
  type ObligationRecord,
  type OpenableRecord,
  clients,
  periods,
} from '../lib/api'
import { ObligationList } from './ObligationList'
import { returnWords } from '../lib/calendar'
import { periodWords } from '../lib/periodWords'
import { clientPath } from '../lib/clientScope'
import { errorReportWords, isUnconfirmed, rejectionWords } from '../lib/filing'
import { advanceEntryWords, advancesStateWords, canEnterAdvance } from '../lib/advances'
import { exportsStateWords } from '../lib/exportFacts'
import { awaitingWords } from '../lib/invoicePreview'
import { STATE_LABELS, artifactWords } from '../lib/returnWords'
import { AmendmentsCard } from './AmendmentsCard'
import { Pmt06Card } from './Pmt06Card'
import { Gstr1aCard } from './Gstr1aCard'
import { GateList } from './GateList'
import { MonthPointsCard } from './MonthPointsCard'
import { ClientGstr1CheckCard } from './ClientGstr1CheckCard'
import { useClient } from './ClientLayout'
import { LifecycleActions } from './LifecycleActions'
import { FILING_STATES, PeriodFilingPanel } from './PeriodFilingPanel'


/** The nil table's rows (`returns/gstr1.py` — the three GSTR-1 Table 8 columns). */
const NIL_ROWS: Record<string, string> = {
  nil_rated: 'Nil-rated supplies',
  exempt: 'Exempt supplies',
  non_gst: 'Non-GST supplies',
}

/** Table 8's four rows as the utility files them (W3-12 (5a)). */
const NIL_SUPPLY_ROWS: Record<string, string> = {
  inter_registered: 'Inter-state supplies to registered persons',
  inter_unregistered: 'Inter-state supplies to unregistered persons',
  intra_registered: 'Intra-state supplies to registered persons',
  intra_unregistered: 'Intra-state supplies to unregistered persons',
}

/** Indian digit grouping — the legacy renders 6,56,755.08, not 656,755.08. */
function rupees(paise: number | undefined): string {
  return ((paise ?? 0) / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * The tax of ONE row, from its own components. Within-row arithmetic over
 * figures the service already computed — never a cross-table sum, which is
 * where a screen would start deciding what a return owes.
 */
function rowTax(t: { igst?: number; cgst?: number; sgst?: number } | undefined): number {
  return (t?.igst ?? 0) + (t?.cgst ?? 0) + (t?.sgst ?? 0)
}

const TABLE_LABELS: Record<string, string> = {
  b2b: 'B2B Invoices — 4A, 4B, 4C, 6B, 6C',
  b2cs: 'B2C (Others) — 7',
  cdnr: 'Credit/Debit Notes (Registered) — 9B',
  hsn: 'HSN/SAC Summary — All Outward Supplies',
  // W3-12 (4a): Table 12 as it is filed since May 2025, in two parts.
  hsn_b2b: 'HSN/SAC Summary — B2B (registered buyers) — 12',
  hsn_b2c: 'HSN/SAC Summary — B2C (everyone else) — 12',
  nil: 'Nil Rated, Exempted — 8A-8D',
  docs: 'Documents Issued — 13',
  // FR-E11 (B-04): Table 11, the utility's own headings.
  at: 'Tax Liability (Advances Received) — 11A',
  txpd: 'Adjustment of Advances — 11B',
}

/**
 * The section tabs, in the legacy's order and wearing its headings.
 *
 * Only sections the draft actually built get a pill: the legacy renders one
 * only when the section has rows, so an absent tab reads the same way to an
 * operator who knows it.
 *
 * **Every table the builder can produce must be listed here**, which it was
 * not. This said B2CL waited on V-01's unregistered threshold and ECO on
 * V-11 — both untrue for some time: `rules/gstr1_tables.py` emits `table:ECO`
 * (rule v4, W3-03) and `table:B2CL` (rule v3, X-04), and `gstr1.py` books both
 * into the draft. So a month with a populated Table 5 or Table 14 got no tab
 * at all, and "an absent tab means no rows" — the convention above — made the
 * rows invisible rather than merely unlisted. `seam-vocabularies.test.ts`
 * checks this against `GSTR1_TABLES`.
 */
const SECTIONS = [
  'b2b',
  'b2cs',
  'b2cl',
  'cdnr',
  'cdnur',
  'eco',
  'hsn',
  'hsn_b2b',
  'hsn_b2c',
  'nil',
  'nil_split',
  'docs',
  // FR-E11 (B-04): Table 11A and 11B, by the JSON's section names.
  'at',
  'txpd',
  // FR-E9 (B-02): Table 6A, exports.
  'exp',
] as const
type Section = (typeof SECTIONS)[number]

/** A tab's pill. `HSN_B2B` is a key, not a word. */
const TAB_WORDS: Partial<Record<Section, string>> = {
  hsn_b2b: 'HSN B2B',
  hsn_b2c: 'HSN B2C',
  nil_split: 'NIL BY SUPPLY',
  at: 'ADVANCES 11A',
  txpd: 'ADJUSTED 11B',
  exp: 'EXPORTS 6A',
}

/** Table 11's columns, 11A and 11B alike: per place and rate, the gross advance and its tax. */
const ADVANCE_COLUMNS = [
  { key: 'pos', label: 'POS' },
  { key: 'rate', label: 'Rate' },
  { key: 'taxable', label: 'Gross advance', money: true },
  { key: 'igst', label: 'IGST', money: true },
  { key: 'cgst', label: 'CGST', money: true },
  { key: 'sgst', label: 'SGST', money: true },
  { key: 'cess', label: 'Cess', money: true },
]

/** Column order per section — the operator reads left to right by habit. */
const COLUMNS: Record<string, Array<{ key: string; label: string; money?: boolean }>> = {
  // FR-E9 (B-02): Table 6A. The export type is the service's code, WPAY or WOPAY — GSTN's
  // own words for the two kinds, as the utility's EXP sheet and the JSON carry them.
  exp: [
    { key: 'export_type', label: 'Type (WPAY / WOPAY)' },
    { key: 'invoice_no', label: 'Inv No' },
    { key: 'invoice_date', label: 'Date' },
    { key: 'shipping_bill_no', label: 'Shipping bill' },
    { key: 'shipping_bill_date', label: 'SB date' },
    { key: 'port_code', label: 'Port' },
    { key: 'taxable', label: 'Taxable', money: true },
    { key: 'rate', label: 'Rate' },
    { key: 'igst', label: 'IGST', money: true },
  ],
  b2b: [
    { key: 'buyer_name', label: 'Customer Name' },
    { key: 'gstin_buyer', label: 'GSTIN' },
    { key: 'pos', label: 'POS' },
    { key: 'invoice_no', label: 'Inv No' },
    { key: 'invoice_date', label: 'Date' },
    { key: 'taxable', label: 'Taxable', money: true },
    { key: 'rate', label: 'Rate' },
    { key: 'igst', label: 'IGST', money: true },
    { key: 'cgst', label: 'CGST', money: true },
    { key: 'sgst', label: 'SGST', money: true },
  ],
  b2cs: [
    { key: 'pos', label: 'POS' },
    { key: 'rate', label: 'Rate' },
    { key: 'taxable', label: 'Taxable Value', money: true },
    { key: 'igst', label: 'IGST', money: true },
    { key: 'cgst', label: 'CGST', money: true },
    { key: 'sgst', label: 'SGST', money: true },
  ],
  b2cl: [
    { key: 'pos', label: 'POS' },
    { key: 'invoice_no', label: 'Inv No' },
    { key: 'invoice_date', label: 'Date' },
    { key: 'taxable', label: 'Taxable', money: true },
    { key: 'rate', label: 'Rate' },
    { key: 'igst', label: 'IGST', money: true },
  ],
  eco: [
    { key: 'pos', label: 'POS' },
    { key: 'rate', label: 'Rate' },
    { key: 'taxable', label: 'Taxable Value', money: true },
    { key: 'igst', label: 'IGST', money: true },
    { key: 'cgst', label: 'CGST', money: true },
    { key: 'sgst', label: 'SGST', money: true },
  ],
  cdnr: [
    { key: 'buyer_name', label: 'Customer Name' },
    { key: 'gstin_buyer', label: 'GSTIN' },
    { key: 'invoice_no', label: 'Note No' },
    { key: 'invoice_date', label: 'Date' },
    { key: 'taxable', label: 'Taxable', money: true },
    { key: 'rate', label: 'Rate' },
    { key: 'igst', label: 'IGST', money: true },
    { key: 'cgst', label: 'CGST', money: true },
    { key: 'sgst', label: 'SGST', money: true },
  ],
  // W3-12 (2): Table 9B, unregistered — notes against B2CL invoices. Signed,
  // as CDNR's are: a credit note's figures are negative.
  at: ADVANCE_COLUMNS,
  txpd: ADVANCE_COLUMNS,
  cdnur: [
    { key: 'type', label: 'Type' },
    { key: 'pos', label: 'POS' },
    { key: 'note_type', label: 'C/D' },
    { key: 'note_no', label: 'Note No' },
    { key: 'note_date', label: 'Date' },
    { key: 'taxable', label: 'Taxable', money: true },
    { key: 'rate', label: 'Rate' },
    { key: 'igst', label: 'IGST', money: true },
  ],
  hsn: [
    { key: 'hsn', label: 'HSN/SAC' },
    { key: 'uqc', label: 'UQC' },
    { key: 'qty', label: 'Qty' },
    { key: 'taxable', label: 'Taxable Value', money: true },
    { key: 'rate', label: 'Rate' },
    { key: 'igst', label: 'IGST', money: true },
    { key: 'cgst', label: 'CGST', money: true },
    { key: 'sgst', label: 'SGST', money: true },
  ],
}
// The two parts of Table 12 carry the same columns as the whole.
COLUMNS.hsn_b2b = COLUMNS.hsn ?? []
COLUMNS.hsn_b2c = COLUMNS.hsn ?? []

/** How many rows a section carries — the number on the legacy's pill. */
function sectionCount(tables: PeriodTables | null, key: Section): number {
  const t = tables?.tables?.[key]
  if (Array.isArray(t)) return t.length
  if (t && typeof t === 'object') return Object.keys(t as object).length
  return 0
}

function cell(row: Record<string, unknown>, col: { key: string; money?: boolean }): string {
  const v = row[col.key]
  if (v === null || v === undefined || v === '') return '—'
  if (col.money && typeof v === 'number') return rupees(v)
  // D-47: a rate the register did not state, worked out from its tax. Said on
  // the row, so a stated rate and a derived one never look the same.
  if (col.key === 'rate' && row.rate_from === 'tax') return `${String(v)} (from tax)`
  return String(v)
}

/**
 * One section of the return, as the legacy's tab shows it: its statutory
 * heading, its own column order, its rows.
 *
 * Rendering only. Every figure was computed by the service; nothing is summed
 * or netted here (ADR-0014). `nil` and `docs` are keyed objects rather than
 * row lists, so they get their own shapes.
 */
function SectionTable({ section, tables }: { section: Section; tables: PeriodTables | null }) {
  const data = tables?.tables?.[section]

  if (section === 'nil' && data && typeof data === 'object') {
    const nil = data as Record<string, number>
    return (
      <div className="gst-table-wrap mb-4">
        <table className="gst-table">
          <thead>
            <tr>
              <th className="is-text">{TABLE_LABELS.nil}</th>
              <th>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(nil).map(([k, v], i) => (
              <tr key={k} className={i % 2 === 1 ? 'is-a' : ''}>
                <td className="is-text is-sec">{NIL_ROWS[k] ?? 'Another untaxed supply'}</td>
                <td>{rupees(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // W3-12 (5a): Table 8 by supply — the four rows the offline utility files. Only rows that
  // carry a figure are in the draft; the workbook states the others as zeros.
  if (section === 'nil_split' && data && typeof data === 'object') {
    const split = data as Record<string, Record<string, number>>
    return (
      <div className="gst-table-wrap mb-4">
        <table className="gst-table">
          <thead>
            <tr>
              <th className="is-text">Nil Rated, Exempted — 8A-8D, by supply</th>
              <th>{NIL_ROWS.nil_rated} (₹)</th>
              <th>{NIL_ROWS.exempt} (₹)</th>
              <th>{NIL_ROWS.non_gst} (₹)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(split).map(([k, v], i) => (
              <tr key={k} className={i % 2 === 1 ? 'is-a' : ''}>
                <td className="is-text is-sec">{NIL_SUPPLY_ROWS[k] ?? k}</td>
                <td>{rupees(v.nil_rated ?? 0)}</td>
                <td>{rupees(v.exempt ?? 0)}</td>
                <td>{rupees(v.non_gst ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  if (section === 'docs' && data && typeof data === 'object') {
    const docs = data as Record<string, Record<string, unknown>>
    return (
      <div className="gst-table-wrap mb-4">
        <table className="gst-table">
          <thead>
            <tr>
              <th className="is-text">Nature</th>
              <th className="is-text">From</th>
              <th className="is-text">To</th>
              <th>Total</th>
              <th>Distinct</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(docs).map(([nature, d], i) => (
              <tr key={nature} className={i % 2 === 1 ? 'is-a' : ''}>
                <td className="is-text is-sec">{nature}</td>
                <td className="is-text">{String(d.from ?? '—')}</td>
                <td className="is-text">{String(d.to ?? '—')}</td>
                <td>{String(d.count ?? 0)}</td>
                <td>{String(d.distinct ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const rows = Array.isArray(data) ? (data as Array<Record<string, unknown>>) : []
  const cols = COLUMNS[section] ?? []
  if (rows.length === 0) return <div className="gst-empty">This section carries no rows.</div>

  return (
    <>
      <p className="gst-sub mb-2">{TABLE_LABELS[section] ?? section.toUpperCase()}</p>
      <div className="gst-table-wrap mb-4">
        <table className="gst-table">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.key} className={c.money ? undefined : 'is-text'}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={i % 2 === 1 ? 'is-a' : ''}>
                {cols.map((c, j) => (
                  <td key={c.key} className={c.money ? undefined : 'is-text'}>
                    {j === 0 ? <span className="font-semibold">{cell(r, c)}</span> : cell(r, c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="gst-meta mb-4">TOTAL ({rows.length} rows)</p>
    </>
  )
}

export function Periods() {
  const client = useClient()
  // This client's due dates (FR-E7): the same read the Calendar screen makes, for one client.
  // A section here rather than a screen of its own, because a person on a client's returns is
  // already asking "what is due" — one call, no new route, no new sidebar item.
  const [obligations, setObligations] = useState<ObligationRecord[]>([])
  useEffect(() => {
    let live = true
    setObligations([])
    periods
      .obligations({ client_id: client.id })
      .then((b) => {
        if (live) setObligations(b.data)
      })
      .catch(() => {
        // The returns themselves still load; a calendar that cannot be read shows nothing
        // rather than a number, and the Calendar screen says why.
      })
    return () => {
      live = false
    }
  }, [client.id])
  const { can } = useEvaluation()

  const [clientRows, setClientRows] = useState<ClientRecord[]>([])
  const [rows, setRows] = useState<PeriodRecord[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [gates, setGates] = useState<PeriodGates | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // The period being typed into **Open a period**. No client in it: this screen is one client's
  // (the URL's), and the card used to offer a select defaulting to the org's FIRST client — a
  // GSTR-1 could be opened for someone else from this client's Returns (N2, 2026-09-29).
  // The return type is chosen on the card (FR-E17, C-02): a GSTR-3B period opens on request,
  // beside the month's GSTR-1. ND-E10 — opening it WITH the GSTR-1 — is the service's seam.
  const [opening, setOpening] = useState<{ period: string } | null>(null)
  // FR-E16: what the client files for the typed month, by its frequency — the service's answer.
  const [openable, setOpenable] = useState<OpenableRecord[]>([])
  const typedMonth = opening?.period ?? ''
  useEffect(() => {
    let live = true
    setOpenable([])
    if (!/^(0[1-9]|1[0-2])-\d{4}$/.test(typedMonth)) return
    periods
      .openable(client.id, typedMonth)
      .then((b) => {
        if (live) setOpenable(b.data)
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof ApiError ? err.message : 'That month could not be read.')
      })
    return () => {
      live = false
    }
  }, [client.id, typedMonth])
  const [snapshotFile, setSnapshotFile] = useState<File | null>(null)
  // FR-E6: the portal's error report, and what landing it answered — shown until the next
  // period is selected, because the sentence names rows the person now goes to Raw Data for.
  const [errorReport, setErrorReport] = useState<ErrorReportUpload | null>(null)
  // FR-E11: one advance typed into the month, and what the last one recorded.
  const [advance, setAdvance] = useState({
    kind: 'advance' as 'advance' | 'adjustment', pos: '', rate: '', amount: '',
  })
  const [advanceEntry, setAdvanceEntry] = useState<AdvanceEntry | null>(null)
  const [snapshotAck, setSnapshotAck] = useState('')
  // The section tabs. `summary` is the legacy's default landing pill.
  const [tab, setTab] = useState<'summary' | Section>('summary')
  const [tables, setTables] = useState<PeriodTables | null>(null)
  // W3-12 (4a): our Table 12 against the client's own HSN summary, when one was uploaded.
  const [clientCheck, setClientCheck] = useState<ClientGstr1Check | null>(null)

  /**
   * Returns standing on a person's word alone (FR-9). Derived from the rows we
   * already hold rather than fetched with `filed_unconfirmed=true`: the server
   * offers that filter and the list screen has every row in hand, so a second
   * round trip would only give the two answers a chance to disagree.
   */
  /** The row the rail has selected, when there is one. */
  const selectedRow = rows.find((r) => r.id === selected) ?? null

  const unconfirmed = rows.filter(isUnconfirmed)

  const load = useCallback(async () => {
    try {
      const [clientBody, periodBody] = await Promise.all([
        clients.list({ limit: 200 }),
        // Only this client's periods (ND-C1) — the URL says which.
        periods.list({ client_id: client.id }),
      ])
      setClientRows(clientBody.data)
      setRows(periodBody.data)
      // Not `current ?? …` — see the note in Gstr3b: a retained selection
      // after a client switch shows the previous client's period.
      setSelected(periodBody.data[0]?.id ?? null)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Returns could not be loaded.')
    }
  }, [client.id])

  const loadGates = useCallback(async () => {
    if (!selected) {
      setGates(null)
      return
    }
    // A SNAPSHOT HAS NO DRAFT, and that is not a failure. `assemble_draft`
    // refuses it deliberately (ND-3), so asking anyway turned the correct
    // answer into a red error banner reading as though something had broken.
    // Found by opening the screen after importing one.
    if (rows.find((r) => r.id === selected)?.origin === 'snapshot') {
      setGates(null)
      setTables(null)
      setClientCheck(null)
      setError(null)
      return
    }
    // Its own read, and its own failure: a comparison that could not be fetched must
    // never take the return's screen down with it.
    periods
      .clientGstr1Check(selected)
      .then((body) => setClientCheck(body.data))
      .catch(() => setClientCheck(null))
    try {
      // Both reads, because the tab strip needs its counts before a tab is
      // opened — the legacy shows the count on the pill, not after the click.
      const [gatesBody, tablesBody] = await Promise.all([
        periods.gates(selected),
        periods.tables(selected),
      ])
      setGates(gatesBody.data)
      setTables(tablesBody.data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The period could not be loaded.')
    }
  }, [selected, rows])

  useEffect(() => {
    void load()
  }, [load])
  useEffect(() => {
    void loadGates()
  }, [loadGates])
  useEffect(() => {
    setErrorReport(null)
    setAdvanceEntry(null)
  }, [selected])

  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true)
      setError(null)
      try {
        await fn()
        await Promise.all([load(), loadGates()])
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That action failed.')
      } finally {
        setBusy(false)
      }
    },
    [load, loadGates],
  )

  const clientName = (id: string) =>
    clientRows.find((c) => c.id === id)?.legal_name ?? id.slice(0, 8)

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="gst-h1">
            <FileSpreadsheet size={20} /> Returns
          </h1>
          <p className="gst-sub">
            The gates panel and the buttons run the same computation — nothing refuses silently
          </p>
        </div>
        {can(PERIOD, 'can_create') && (
          <Button
            type="button"
            onClick={() => setOpening({ period: '' })}
            size="sm"
          >
            <Plus size={16} /> Open a period
          </Button>
        )}
      </header>

      {error && (
        <Alert tone="error" className="mb-4">{error}</Alert>
      )}

      {opening && (
        <Card className="mb-6 flex flex-wrap items-end gap-3 p-4" data-testid="open-a-period">
          <label className="gst-label">
            Client
            <span className="gst-input is-fixed">{client.legal_name}</span>
          </label>
          <label className="gst-label">
            Month (MM-YYYY)
            <input
              value={opening.period}
              onChange={(e) => setOpening({ ...opening, period: e.target.value })}
              placeholder="08-2026"
              className="gst-input w-28"
            />
          </label>
          {/*
            * One button per return the client files for that month (FR-E17, FR-E16): the
            * service answers by the client's filing frequency — a monthly client's GSTR-1 and
            * GSTR-3B; a quarterly client's IFF (months 1 and 2) and the quarter's GSTR-1 and
            * GSTR-3B. The screen decides none of it. Opening is idempotent on the service, so a
            * period already open simply comes back.
            */}
          {openable.map((o, index) => (
            <Button
              key={`${o.return_type}:${o.period}`}
              type="button"
              disabled={busy}
              onClick={() =>
                void act(() =>
                  periods.open({
                    client_id: client.id,
                    return_type: o.return_type,
                    period: o.period,
                    period_type: o.period_type,
                  }),
                ).then(() => setOpening(null))
              }
              size="sm"
              variant={index === 0 ? 'primary' : 'outline'}
            >
              Open {o.label}
            </Button>
          ))}
          <Button
            type="button"
            onClick={() => setOpening(null)}
            variant="outline" size="sm"
          >
            Cancel
          </Button>
        </Card>
      )}

      {/*
        * FR-9's list. Returns MARKED filed on somebody's acknowledgment number
        * and never confirmed by the portal's own receipt. It sits above the
        * list rather than inside it because the question is asked of the whole
        * book at once: "which of these do we only have our own word for?"
        * Rendered only when there is something outstanding.
        */}
      {unconfirmed.length > 0 && (
        <Alert tone="warning" className="mb-4">
          <p className="gst-panel-h">
            {unconfirmed.length === 1
              ? 'One return is filed on an acknowledgment number alone'
              : `${unconfirmed.length} returns are filed on an acknowledgment number alone`}
          </p>
          <p className="gst-meta mb-2">
            Select one and upload the portal&rsquo;s filed return (the <code>.json</code>{' '}
            from Returns Dashboard → Download Filed Return) to confirm it. An ARN is what
            someone typed; the receipt is what the portal said.
          </p>
          <ul className="space-y-1">
            {unconfirmed.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className="gst-link"
                  onClick={() => setSelected(row.id)}
                >
                  {returnWords(row.return_type)} · {periodWords(row.period)}
                  {row.filed_ack_no ? ` · ${row.filed_ack_no}` : ''}
                </button>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/*
        * X-05. A month filed before ProGST held these books: stored, shown,
        * NEVER recomputed. There is no period to pick — the filed return names
        * its own month, and a caller who names a different one is refused.
        */}
      {can(PERIOD, 'can_create') && (
        <Card className="mb-4 p-3">
          <p className="gst-panel-h">Import a month filed before ProGST</p>
          <p className="gst-meta mb-2">
            The portal&rsquo;s filed return (Returns Dashboard → GSTR-1 → Download Filed
            Return). It is kept as a record of what was filed and is never recomputed
            here, so an amendment has something to point at.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="file"
              accept="application/json,.json"
              onChange={(e) => setSnapshotFile(e.target.files?.[0] ?? null)}
              className="gst-input"
            />
            <input
              value={snapshotAck}
              onChange={(e) => setSnapshotAck(e.target.value.slice(0, 64))}
              placeholder="Acknowledgment number (optional)"
              className="gst-input w-64"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!snapshotFile || busy}
              onClick={() =>
                void act(async () => {
                  if (!snapshotFile) return
                  await periods.importSnapshot({
                    client_id: client.id,
                    file: snapshotFile,
                    ...(snapshotAck.trim() ? { ack_no: snapshotAck.trim() } : {}),
                  })
                  setSnapshotFile(null)
                  setSnapshotAck('')
                })
              }
            >
              Import as a snapshot
            </Button>
          </div>
        </Card>
      )}

      {obligations.length > 0 && (
        <Card className="mb-6 p-4" data-testid="client-calendar">
          <p className="gst-panel-h mb-2">Due dates</p>
          <ObligationList rows={obligations} showClient={false} />
        </Card>
      )}

      <div className="flex flex-wrap gap-6">
        {/* -- the period list ---------------------------------------------- */}
        <aside className="w-72 shrink-0">
          {rows.length === 0 ? (
            <div className="gst-empty">No periods yet.</div>
          ) : (
            <ul className="space-y-1">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(row.id)}
                    aria-current={row.id === selected}
                    className="gst-rail-item"
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate">{clientName(row.client_id)}</span>
                      <span
                        className={`gst-state ${
                          row.state === 'locked' || row.state === 'filed'
                            ? 'is-go'
                            : row.state === 'open'
                              ? ''
                              : 'is-go'
                        }`}
                      >
                        {STATE_LABELS[row.state] ?? row.state}
                      </span>
                    </span>
                    <span className="mt-0.5 block text-[11px]">
                      {returnWords(row.return_type)} · {periodWords(row.period)}
                      {/*
                        * A snapshot is NOT a period being worked — it is a
                        * record of what was filed before ProGST held these
                        * books, and nothing about it can be recomputed. Saying
                        * so in the rail is what stops someone selecting it and
                        * wondering why every action refuses.
                        */}
                      {row.origin === 'snapshot' && ' · snapshot'}
                      {/*
                        * Marked filed on an acknowledgment number alone (FR-9).
                        * A person's word, not the portal's receipt.
                        */}
                      {isUnconfirmed(row) && ' · unconfirmed'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* -- the detail: gates, totals, actions --------------------------- */}
        <section className="min-w-0 flex-1">
          {selectedRow?.origin === 'snapshot' ? (
            <Card className="p-4">
              <p className="gst-panel-h">
                {selectedRow.period} — a return filed before ProGST held these books
              </p>
              <p className="gst-meta">
                Kept as a record of what the portal says was filed. It is{' '}
                <strong>never recomputed here</strong>, so there are no gates and no
                draft: nothing on this screen can change what this month says.
                {selectedRow.filed_ack_no
                  ? ` Acknowledgment ${selectedRow.filed_ack_no}.`
                  : ''}
              </p>
              <p className="gst-meta mt-2">
                An amendment to a later period can reference it — that is what it is
                here for: open the later month and choose {selectedRow.period} under
                Amendments to an earlier month.
              </p>
            </Card>
          ) : !gates ? (
            <p className="gst-sub">Select a period.</p>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {gates.client_name} — {returnWords(gates.period.return_type)}{' '}
                    {periodWords(gates.period.period)}
                  </p>
                  <p className="gst-meta">
                    {STATE_LABELS[gates.period.state] ?? gates.period.state}
                    {gates.period.filed_ack_no && ` · ACK ${gates.period.filed_ack_no}`}
                  </p>
                </div>
                {can(PERIOD, 'can_update') && (
                  <LifecycleActions
                    key={gates.period.id}
                    period={gates.period}
                    artifacts={gates.artifacts}
                    busy={busy}
                    act={act}
                    onErrorReport={setErrorReport}
                  />
                )}
              </div>

              {/* D-07..D-09, D-18: the return through the filing lane, from client approval on. */}
              {FILING_STATES.has(gates.period.state) && (
                <PeriodFilingPanel
                  key={`filing:${gates.period.id}`}
                  periodId={gates.period.id}
                  clientId={client.id}
                  onChanged={() => void loadGates()}
                />
              )}

              {errorReport && (
                <Alert tone={errorReport.errors_unmatched ? 'warning' : 'success'} className="mb-4">
                  <p>{errorReportWords(errorReport)}</p>
                  {errorReport.unmatched.length > 0 && (
                    <>
                      <p className="gst-meta mt-2">
                        Not matched to any row here — the portal named these and ProGST
                        could not place them:
                      </p>
                      <ul className="gst-meta list-disc pl-5">
                        {errorReport.unmatched.map((u, i) => (
                          <li key={i}>{rejectionWords(u)}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {errorReport.rows_held > 0 && (
                    <p className="gst-meta mt-2">
                      Each held row shows the portal's message on{' '}
                      <Link to={clientPath(client.id, 'review')} className="gst-link">
                        Raw Data
                      </Link>
                      ; a correction releases it.
                    </p>
                  )}
                </Alert>
              )}

              {/* the money cards — one table's own figures, never a cross-table sum */}
              <div className="gst-stats mb-4">
                <div className="gst-stat">
                  <div className="n is-money">₹{rupees(gates.totals.b2b?.taxable)}</div>
                  <div className="l">B2B TAXABLE</div>
                </div>
                <div className="gst-stat">
                  <div className="n is-money is-tax">₹{rupees(rowTax(gates.totals.b2b))}</div>
                  <div className="l">B2B TAX</div>
                </div>
                <div className="gst-stat">
                  <div className="n">
                    {gates.blocked_table12.length + gates.tieout.length + (gates.awaiting_approval?.length ?? 0)}
                  </div>
                  <div className="l">BLOCKERS</div>
                </div>
                <div className="gst-stat">
                  <div className="n">{gates.excluded.length}</div>
                  <div className="l">HELD OUT</div>
                </div>
              </div>

              {/* the gates panel — real blockers, verbatim, in the legacy's alert dress */}
              <Alert tone={gates.blocks_prepared ? 'warning' : 'success'} className="mb-4">
                <p className="gst-panel-h">
                  Gates
                  <span className={`gst-tag ${gates.blocks_prepared ? '' : 'is-clear'}`}>
                    {gates.blocks_prepared ? 'blocked · prepare refused' : 'clear'}
                  </span>
                </p>
                {gates.blocked_table12.length === 0 &&
                (gates.marked_table12?.length ?? 0) === 0 &&
                gates.tieout.length === 0 &&
                (gates.awaiting_approval?.length ?? 0) === 0 &&
                gates.pending.length === 0 &&
                gates.excluded.length === 0 ? (
                  <p className="text-sm">Nothing blocks this return. Every reviewed row is in.</p>
                ) : (
                  <ul className="gst-panel-list">
                    {(gates.awaiting_approval?.length ?? 0) > 0 && (
                      <li>
                        <span className="gst-k">Awaiting approval</span>
                        <span>
                          {awaitingWords(gates.awaiting_approval!)} Until a person approves them they are not in
                          this return, and Prepare is refused.{' '}
                          <Link to={clientPath(gates.period.client_id, 'intake')}>Approve on Upload Data</Link>.
                        </span>
                      </li>
                    )}
                    <GateList key={`${gates.period.id}:Table 12`} label="Table 12" consequence="blocks prepared"
                      rows={gates.blocked_table12} />
                    <GateList key={`${gates.period.id}:Table 12, marked`} label="Table 12, marked"
                      consequence="in the return; a code GSTN does not publish stays out of the utility and the JSON" soft
                      rows={gates.marked_table12 ?? []} />
                    {gates.tieout.map((t, i) => (
                      <li key={`t${i}`}>
                        <span className="gst-k">Tie-out</span>
                        <span>{String(t.message)}</span>
                      </li>
                    ))}
                    {/*
                      * A row a PERSON took out is not a row a check held — the
                      * old line read "row abc123 () — joins on fix", empty
                      * parentheses and an instruction to fix something nobody
                      * broke (2026-09-09). Two groups, and the service's `why`
                      * says which: "Removed by the preparer: <note>".
                      */}
                    <GateList key={`${gates.period.id}:Held out`} label="Held out" consequence="joins the return once fixed" soft
                      rows={gates.excluded.filter((e) => e.excluded_by !== 'reviewer')} />
                    <GateList key={`${gates.period.id}:Removed by the preparer`} label="Removed by the preparer" consequence="not in the return" soft
                      rows={gates.excluded.filter((e) => e.excluded_by === 'reviewer')} />
                    <GateList key={`${gates.period.id}:Awaiting verification`} label="Awaiting verification" consequence="not in the return yet" soft
                      rows={gates.pending} />
                  </ul>
                )}
              </Alert>

              {/* the section tabs — the legacy's flow, pill per section with its
                  count, rendered only for sections the draft actually built */}
              <div className="gst-tabs mb-3">
                <button
                  type="button"
                  className="gst-tab"
                  aria-selected={tab === 'summary'}
                  onClick={() => setTab('summary')}
                >
                  Summary
                </button>
                {SECTIONS.filter((k) => sectionCount(tables, k) > 0).map((k) => (
                  <button
                    key={k}
                    type="button"
                    className="gst-tab"
                    aria-selected={tab === k}
                    onClick={() => setTab(k)}
                  >
                    {TAB_WORDS[k] ?? k.toUpperCase()}
                    <span className="gst-count">{sectionCount(tables, k)}</span>
                  </button>
                ))}
              </div>

              {/*
                * FR-E11: Table 11 with nothing in it is a STATE the service names, never
                * an empty tab read as nil. Said once, on the summary.
                */}
              {tab === 'summary' && exportsStateWords(tables?.exports) && (
                <p className="gst-meta mb-3" data-testid="exports-state">
                  {exportsStateWords(tables?.exports)}
                </p>
              )}
              {tab === 'summary' && advancesStateWords(tables?.advances) && (
                <p className="gst-meta mb-3" data-testid="advances-state">
                  {advancesStateWords(tables?.advances)}
                </p>
              )}

              {tab !== 'summary' && (
                <SectionTable section={tab} tables={tables} />
              )}

              {(tab === 'summary' || tab === 'hsn' || tab === 'hsn_b2b' || tab === 'hsn_b2c') && (
                <ClientGstr1CheckCard check={clientCheck} />
              )}

              {/* the return, in numbers — the legacy's section-banded summary */}
              {tab === 'summary' && (
              <div className="gst-table-wrap mb-4">
                <table className="gst-table">
                  <thead>
                    <tr>
                      <th className="is-text">Section</th>
                      <th className="is-text">Table</th>
                      <th>Taxable (₹)</th>
                      <th>IGST (₹)</th>
                      <th>CGST (₹)</th>
                      <th>SGST (₹)</th>
                      <th>Tax (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(['b2b', 'b2cs', 'cdnr'] as const).map((table, i) => (
                      <tr key={table} className={i % 2 === 0 ? 'is-a' : ''}>
                        <td className="is-sec">{table.toUpperCase()}</td>
                        <td className="is-text">{TABLE_LABELS[table]}</td>
                        <td>{rupees(gates.totals[table]?.taxable)}</td>
                        <td>{rupees(gates.totals[table]?.igst)}</td>
                        <td>{rupees(gates.totals[table]?.cgst)}</td>
                        <td>{rupees(gates.totals[table]?.sgst)}</td>
                        <td>{rupees(rowTax(gates.totals[table]))}</td>
                      </tr>
                    ))}
                    {/* The legacy's closing pair, computed service-side. */}
                    <tr className="is-net">
                      <td className="is-sec">NET OUTWARD</td>
                      <td className="is-text">(excl. NIL/Exempt)</td>
                      <td>{rupees(gates.totals.net_outward?.taxable)}</td>
                      <td>{rupees(gates.totals.net_outward?.igst)}</td>
                      <td>{rupees(gates.totals.net_outward?.cgst)}</td>
                      <td>{rupees(gates.totals.net_outward?.sgst)}</td>
                      <td>{rupees(rowTax(gates.totals.net_outward))}</td>
                    </tr>
                    <tr className="is-gross">
                      <td className="is-sec">GROSS TOTAL</td>
                      <td className="is-text">(incl. NIL/Exempt/Non-GST)</td>
                      <td>{rupees(gates.totals.gross_total?.taxable)}</td>
                      <td>{rupees(gates.totals.gross_total?.igst)}</td>
                      <td>{rupees(gates.totals.gross_total?.cgst)}</td>
                      <td>{rupees(gates.totals.gross_total?.sgst)}</td>
                      <td>{rupees(rowTax(gates.totals.gross_total))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              )}

              {/*
                * FR-E13 (B-07): on a filed GSTR-1, what has changed since filing and the door
                * to the month's GSTR-1A; on a 1A, what it corrects and the way back.
                */}
              <Gstr1aCard
                key={`1a:${gates.period.id}`}
                gates={gates}
                canCreate={can(PERIOD, 'can_create')}
                busy={busy}
                onSelect={setSelected}
                onOpen={() => {
                  let opened: string | null = null
                  void act(async () => {
                    opened = (await periods.openGstr1a(gates.period.id)).data.id
                  }).then(() => {
                    if (opened) setSelected(opened)
                  })
                }}
              />

              {(gates.artifacts.length > 0 ||
                (gates.period.return_type === 'GSTR1' && can(PERIOD, 'can_update'))) && (
                <Card className="p-3">
                  {gates.artifacts.map((a) => (
                    <p key={`${a.kind}v${a.version_no}`} className="gst-meta">
                      {artifactWords(a, gates.artifacts)}
                    </p>
                  ))}
                  {/*
                    * FR-E2 (e2e-filing A-06): the approval stood, the GSTN JSON did not — the
                    * draft books a section the writer has none for. Said in the words the
                    * audit event and Activity use; gone once a JSON is written.
                    */}
                  {gates.gstn_json_hold && (
                    <Alert tone="warning" className="mt-2">
                      {gates.gstn_json_hold.words}
                    </Alert>
                  )}
                  {/*
                    * D-59: made at Prepare, and again here whenever the data changed since —
                    * each time the next version, every earlier one kept (D-58).
                    */}
                  {gates.period.return_type === 'GSTR1' && can(PERIOD, 'can_update') && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      disabled={busy}
                      onClick={() =>
                        void act(() => periods.generateOfflineUtility(gates.period.id))
                      }
                    >
                      <FileSpreadsheet size={12} /> Generate the offline-utility workbook
                    </Button>
                  )}
                  {gates.period.return_type === 'GSTR1' && can(PERIOD, 'can_update') && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="ml-2 mt-2"
                      disabled={busy}
                      onClick={() => void act(() => periods.generateWorkingSheet(gates.period.id))}
                    >
                      <FileSpreadsheet size={12} /> Generate the working sheet
                    </Button>
                  )}
                  {/*
                    * FR-E1 (e2e-filing A-02): the portal payload is made when the client
                    * approves, and again here on demand after that. Offered only once the
                    * client has approved — before that the service refuses, naming the state
                    * (FR-44), and a button that can only refuse is noise.
                    */}
                  {gates.period.return_type === 'GSTR1' &&
                    can(PERIOD, 'can_update') &&
                    ['client_approved', 'filed', 'locked'].includes(gates.period.state) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="ml-2 mt-2"
                        disabled={busy}
                        onClick={() => void act(() => periods.generateGstnJson(gates.period.id))}
                      >
                        <FileJson size={12} /> Generate the GSTN JSON
                      </Button>
                    )}
                  {/* FR-E13 (B-07): the 1A's own payload, on the same rule (FR-44). */}
                  {gates.period.return_type === 'GSTR1A' &&
                    can(PERIOD, 'can_update') &&
                    ['client_approved', 'filed', 'locked'].includes(gates.period.state) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        disabled={busy}
                        onClick={() => void act(() => periods.generateGstr1aJson(gates.period.id))}
                      >
                        <FileJson size={12} /> Generate the GSTR-1A JSON
                      </Button>
                    )}
                </Card>
              )}

              {/*
                * FR-E11 (B-04): an advance typed for a month that keeps no advances
                * register. The service stores it as a one-line document in the Advances
                * lane, reviews it like a register's row, audits it, and sends a month
                * already out for approval back to Prepared (FR-7).
                */}
              {canEnterAdvance(gates.period) && can(PERIOD, 'can_update') && (
                <Card className="mb-4 p-3" data-testid="enter-advance">
                  <p className="gst-panel-h">Enter an advance (Table 11)</p>
                  <p className="gst-meta mb-2">
                    For a month with no advances register: an advance received against a
                    supply not yet invoiced, or the adjustment of one against this
                    month&rsquo;s invoice. The amount is the gross advance, excluding tax;
                    the tax is worked out from the rate and the place of supply.
                  </p>
                  <div className="flex flex-wrap items-end gap-2">
                    <label className="gst-label">
                      Kind
                      <select
                        value={advance.kind}
                        onChange={(e) =>
                          setAdvance({
                            ...advance, kind: e.target.value as 'advance' | 'adjustment',
                          })
                        }
                        className="gst-input"
                      >
                        <option value="advance">Advance received (11A)</option>
                        <option value="adjustment">Advance adjusted against an invoice (11B)</option>
                      </select>
                    </label>
                    <label className="gst-label">
                      Place of supply (state code)
                      <input
                        value={advance.pos}
                        onChange={(e) => setAdvance({ ...advance, pos: e.target.value })}
                        placeholder="27"
                        className="gst-input w-20"
                      />
                    </label>
                    <label className="gst-label">
                      Rate (%)
                      <input
                        value={advance.rate}
                        onChange={(e) => setAdvance({ ...advance, rate: e.target.value })}
                        placeholder="18"
                        className="gst-input w-20"
                      />
                    </label>
                    <label className="gst-label">
                      Amount (₹, excluding tax)
                      <input
                        value={advance.amount}
                        onChange={(e) => setAdvance({ ...advance, amount: e.target.value })}
                        placeholder="50000.00"
                        className="gst-input w-32"
                      />
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={
                        busy ||
                        !/^\d{2}$/.test(advance.pos.trim()) ||
                        advance.rate.trim() === '' ||
                        !(Number(advance.rate) >= 0) ||
                        !(Number(advance.amount) > 0)
                      }
                      onClick={() =>
                        void act(async () => {
                          const body = await periods.enterAdvance(gates.period.id, {
                            kind: advance.kind,
                            pos: advance.pos.trim(),
                            rate: Number(advance.rate),
                            amount: advance.amount.trim(),
                          })
                          setAdvanceEntry(body.data)
                          setAdvance({ ...advance, amount: '' })
                        })
                      }
                    >
                      <Plus size={12} /> Enter the advance
                    </Button>
                  </div>
                  {advanceEntry && (
                    <Alert tone="success" className="mt-2">
                      {advanceEntryWords(advanceEntry)}
                    </Alert>
                  )}
                </Card>
              )}

              {/*
                * Finding A2 (stage 5e). Only a GSTR-1 has amendment tables,
                * and only against an earlier month — the card offers the ones
                * whose filed return is held.
                */}
              {/*
                * D-130: a point about the month as a whole, for the client workbook's
                * To confirm. A snapshot is a record and has nothing to raise.
                */}
              {gates.period.return_type === 'GSTR1' && gates.period.origin !== 'snapshot' && (
                <MonthPointsCard
                  key={`points-${gates.period.id}`}
                  periodId={gates.period.id}
                  canRaise={can(PERIOD, 'can_update')}
                  busy={busy}
                  act={act}
                />
              )}

              {gates.period.return_type === 'GSTR1' && (
                <AmendmentsCard key={gates.period.id} period={gates.period} rows={rows} />
              )}
              {/* FR-E16: a quarterly 3B's first two months are paid by PMT-06 deposits. */}
              {gates.period.return_type === 'GSTR3B' &&
                gates.period.period_type === 'quarterly' && (
                  <Pmt06Card
                    key={`pmt06:${gates.period.id}`}
                    clientId={gates.period.client_id}
                    quarter={gates.period.period}
                  />
                )}
            </>
          )}
        </section>
      </div>
    </div>
  )
}
